import React, { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import classnames from "classnames";
import moment from "moment";
import "moment-duration-format";
import { getSummonerProfileContext } from "components/SummonerProfiles/SummonerProfileContext";
import { TooltipContainer } from "@outplayed/tooltips";
import HistoricRanks from "components/common/HistoricRanks/HistoricRanks";
import UpdateButton from "components/SummonerProfiles/views/ProfileHeader/UpdateButton";
import FavoriteSummonerButton from "components/SummonerProfiles/views/ProfileHeader/FavoriteSummonerButton";
import { selectRank, getTierColor } from "@ugg/shared/utils/rank-helpers";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { useSummonerProfile, useProfileOverallRanking } from "api/summonerProfileApi";
import { numberWithCommas } from "lib/general-helper";
import { INTERNAL_CURRENT_SEASON } from "@ugg/shared/constants/seasons";
import { ReactComponent as BoostIcon } from "svg/boost-icon.svg";
import { ReactComponent as Verified } from "svg/verified.svg";

const UPDATE_OUTDATED_TEXT = "a long time ago";
function getLastModifiedFromNow(lastModified) {
  if (!lastModified) return UPDATE_OUTDATED_TEXT;

  return moment(lastModified * 1000).fromNow();
}

const overXHoursAgo = (fromNowString, x = 12) => {
  if (fromNowString === UPDATE_OUTDATED_TEXT) return true;
  if (fromNowString.toLowerCase().indexOf("minute") !== -1) return false;
  if (fromNowString.toLowerCase().indexOf("second") !== -1) return false;
  if (fromNowString.toLowerCase().indexOf("hour") === -1) return true;
  const num = fromNowString.split()[0];

  if (parseInt(num) >= x) return true;
  return false;
};

const MemberStatus = ({ status }) => {
  if (status == "premium") {
    return (
      <TooltipContainer tooltipInfo={`BOOST user account`}>
        <div className="align-items-center">
          <BoostIcon className="boost-icon" />
        </div>
      </TooltipContainer>
    );
  } else if (status == "verified") {
    return (
      <TooltipContainer tooltipInfo={`Claimed Account`}>
        <div className="verified-badge">
          <img src={"https://static.bigbrain.gg/assets/ugg/icons/verified.svg"} alt={"Claimed account"} />
        </div>
      </TooltipContainer>
    );
  } else {
    return (
      <TooltipContainer tooltipInfo={`Unclaimed Account`}>
        <Link to="/signup?redirect_url=/verification" className="flex-center">
          <img src={"https://static.bigbrain.gg/assets/ugg/icons/icon-unclaimed.svg"} alt={"Unclaimed account"} />
        </Link>
      </TooltipContainer>
    );
  }
};

const SummonerProfileHeader = (props) => {
  const context = getSummonerProfileContext();
  const { getProfileIconImg } = getRiotAssetsContext();
  const { summonerName, region, previewMode } = context;
  const tooltipRef = useRef();

  const { data: profileData, loading: loadingProfile, error: errorProfile } = useSummonerProfile(region, summonerName, {}, "");
  const { data: rankingData, loading: loadingRanking, error: errorRanking } = useProfileOverallRanking(summonerName, region, 420);

  if (!profileData || loadingProfile || errorProfile) {
    return null;
  }

  const { profileInit, fetchProfileRanks } = profileData;
  const { playerInfo, memberStatus, lastModified } = profileInit || {};
  const { rankScores } = fetchProfileRanks || {};
  const {
    summonerName: realSummonerName, // correct spelling
    regionId,
    iconId,
    summonerLevel,
  } = playerInfo || {};

  const rankToDisplay = selectRank("all", INTERNAL_CURRENT_SEASON, rankScores);
  const lastUpdatedFromNow = getLastModifiedFromNow(lastModified);
  const outOfDate = overXHoursAgo(lastUpdatedFromNow, 12);
  const { tier } = rankToDisplay || {};
  const tierColor = getTierColor(tier);

  const { overallRanking } = rankingData || {};
  const { overallRanking: ladderRanking, totalPlayerCount } = overallRanking || {};
  const rankPercentile = useMemo(() => {
    if (!ladderRanking || !totalPlayerCount) return null;

    let percentile = (ladderRanking / totalPlayerCount) * 100;
    percentile = percentile > 1 ? Math.round(percentile * 10) / 10 : Math.round(percentile * 10000) / 10000;
    return percentile;
  }, [rankingData]);

  return (
    <div className="summoner-profile_header">
      <HistoricRanks summonerName={summonerName} regionId={region} queueType={420} />
      <div className="summoner-profile_main">
        <div className="profile-icon-container" style={{ borderColor: tierColor }}>
          {summonerLevel && (
            <div className="level-header" style={{ borderColor: tierColor }}>
              {summonerLevel}
            </div>
          )}
          <div className="profile-icon-border">
            {summonerLevel && <div className="border-notch" />}
            <img className="profile-icon-image" src={getProfileIconImg(iconId)} />
          </div>
          <FavoriteSummonerButton
            summonerName={realSummonerName}
            regionId={regionId}
            iconId={iconId}
            iconUrl={getProfileIconImg(iconId)}
          />
        </div>
        <div className={classnames("summoner-profile_info", { "preview-mode": previewMode })}>
          <div className="summoner-profile_info_name">
            <div className="summoner-name">
              <span>{realSummonerName}</span>
            </div>
            <div className="member-status">
              <MemberStatus status={memberStatus} />
            </div>
          </div>
          <div className="ladder-ranking">
            {ladderRanking && (
              <TooltipContainer tooltipInfo={"View your current ladding ranking"}>
                <Link to={`/leaderboards/ranking?summonerName=${summonerName}&region=${region}`}>
                  <span>
                    Ladder Rank <strong>{numberWithCommas(ladderRanking)}</strong> (top {rankPercentile}%)
                  </span>
                </Link>
              </TooltipContainer>
            )}
          </div>
          <div className="profile-info-button-group">
            <TooltipContainer ref={tooltipRef} tooltipInfo={`Last Updated: ${lastUpdatedFromNow}`}>
              <div onClick={() => tooltipRef.current && tooltipRef.current.forceClose()}>
                <UpdateButton outOfDate={outOfDate} />
              </div>
            </TooltipContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummonerProfileHeader;
